import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Box, Typography, Dialog, DialogContent, Grow } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessIcon from "@mui/icons-material/Business";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import {
  ThemeScope, Button, Input, Label,
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
  Alert, Badge, Separator,
} from "./ui/shadcn";

// Design tokens (Design 13 - navy sidebar + light enterprise form)
const INK = "#0b1220";
const MUTED = "#566478";
const NAVY = "#0a1a3a";
const EASE = [0.22, 1, 0.36, 1];

const THEME_VARS = {
  "--background": "#ffffff",
  "--foreground": INK,
  "--card": "#ffffff",
  "--card-foreground": INK,
  "--primary": "#1d4ed8",
  "--primary-foreground": "#ffffff",
  "--secondary": "#e8f0fe",
  "--secondary-foreground": "#1e3a8a",
  "--muted": "#f1f5f9",
  "--muted-foreground": MUTED,
  "--accent": "#eef4ff",
  "--destructive": "#b91c1c",
  "--destructive-foreground": "#ffffff",
  "--border": "#d5dde9",
  "--input": "#8fa1bc",
  "--ring": "#2563eb",
  "--radius": "0.625rem",
};

const MotionButton = motion.create(Button);
const MotionCard = motion.create(Card);

const CSS = [
  "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap&family=IBM+Plex+Mono:wght@400;500;600');",
  ".login13-root, .login13-root * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif !important; }",
  ".login13-root .ld13-mono { font-family: 'IBM Plex Mono', ui-monospace, Consolas, monospace !important; }",

  /* Page shell: stacked by default, side-by-side from 1024px */
  ".ld13-page { position: relative; width: 100%; min-height: 100vh; display: flex; flex-direction: column; overflow-x: hidden; background: #f2f5fa; }",
  "@supports (min-height: 100dvh) { .ld13-page { min-height: 100dvh; } }",
  "@media (min-width: 1024px) { .ld13-page { flex-direction: row; } }",

  /* Compact brand bar (below 1024px) */
  ".ld13-mbar { position: relative; flex-shrink: 0; overflow: hidden; background: " + NAVY + "; color: #fff; padding: 14px clamp(16px, 4vw, 32px); }",
  ".ld13-mbar-deco { position: absolute; inset: 0; z-index: 0; pointer-events: none; background: radial-gradient(ellipse 70% 120% at 100% 0%, rgba(37,99,235,0.4) 0%, transparent 70%); }",
  ".ld13-mbar-top { position: relative; z-index: 1; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }",
  ".ld13-mbar-chips { position: relative; z-index: 1; display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 8px; list-style: none; margin: 14px 0 0; padding: 0; max-width: 520px; }",
  ".ld13-chip { height: 44px; min-width: 0; border-radius: 12px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.18); color: #c3d8ff; }",
  "@media (max-height: 480px) { .ld13-mbar-chips { display: none; } .ld13-mbar { padding-top: 10px; padding-bottom: 10px; } }",
  "@media (min-width: 1024px) { .ld13-mbar { display: none; } }",

  /* Sidebar */
  ".ld13-side { display: none; position: relative; overflow: hidden; flex: 0 0 auto; width: clamp(340px, 40%, 640px); background: linear-gradient(170deg, #0a1a3a 0%, #08142e 55%, #0b2257 140%); color: #fff; }",
  "@media (min-width: 1024px) { .ld13-side { display: flex; } }",
  ".ld13-side-deco { position: absolute; inset: 0; z-index: 0; pointer-events: none; background-image: radial-gradient(rgba(147,197,253,0.10) 1px, transparent 1px), radial-gradient(ellipse 80% 55% at 100% 0%, rgba(37,99,235,0.38) 0%, transparent 70%), radial-gradient(ellipse 70% 45% at 0% 100%, rgba(14,165,233,0.18) 0%, transparent 70%); background-size: 24px 24px, 100% 100%, 100% 100%; }",
  ".ld13-side-inner { position: relative; z-index: 1; display: flex; flex-direction: column; justify-content: space-between; gap: 28px; width: 100%; min-width: 0; padding: clamp(24px, 3.4vw, 64px); box-sizing: border-box; }",
  ".ld13-logo { display: block; height: clamp(34px, 3.2vw, 48px); width: auto; max-width: 100%; filter: brightness(0) invert(1); }",
  ".login13-root .ld13-eyebrow { background: rgba(56,189,248,0.12); border-color: rgba(147,197,253,0.4); color: #cfe2ff; gap: .5rem; font-size: .68rem; letter-spacing: 1.4px; text-transform: uppercase; padding: .35rem .8rem; max-width: 100%; }",
  ".ld13-dot { width: 7px; height: 7px; border-radius: 50%; background: #4ade80; flex-shrink: 0; }",
  ".ld13-h1 { font-size: clamp(1.9rem, 2.8vw, 3rem); font-weight: 700; line-height: 1.12; letter-spacing: -.8px; margin: 18px 0 12px; color: #fff; overflow-wrap: anywhere; }",
  ".ld13-h1 em { font-style: normal; color: #86bbff; }",
  ".ld13-lead { color: #b9c8e3; font-size: clamp(.88rem, 1vw, .98rem); line-height: 1.65; margin: 0; max-width: 40ch; }",
  ".ld13-tiles { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: clamp(10px, 1vw, 16px); }",
  ".ld13-tile { position: relative; height: 100%; box-sizing: border-box; display: flex; flex-direction: column; gap: 12px; padding: clamp(12px, 1.1vw, 18px); border-radius: 14px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.14); min-width: 0; }",
  ".ld13-tile::before { content: ''; position: absolute; inset: -1px; border-radius: inherit; border: 1px solid #60a5fa; background: rgba(37,99,235,0.2); opacity: 0; transition: opacity .2s ease; pointer-events: none; z-index: 0; }",
  ".ld13-tile:hover::before { opacity: 1; }",
  ".ld13-tile > * { position: relative; z-index: 1; }",
  ".ld13-tile-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }",
  ".ld13-tile-ico { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; background: rgba(96,165,250,0.18); border: 1px solid rgba(147,197,253,0.3); color: #b3d3ff; flex-shrink: 0; }",
  ".ld13-tile-idx { font-size: .68rem; color: #94a8cc; letter-spacing: 1px; }",
  ".ld13-tile-name { font-size: .94rem; font-weight: 600; color: #fff; line-height: 1.2; overflow-wrap: anywhere; }",
  ".ld13-tile-sub { font-size: .77rem; color: #b0bfda; line-height: 1.4; margin-top: 3px; overflow-wrap: anywhere; }",
  ".ld13-side-foot { display: flex; flex-direction: column; gap: 16px; }",
  ".ld13-side .ui-sep { background: rgba(255,255,255,0.16); }",
  ".ld13-pills { display: flex; flex-wrap: wrap; gap: 8px; }",
  ".login13-root .ld13-pill { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.2); color: #dbe6f7; font-weight: 500; }",
  "@media (min-width: 1024px) and (max-height: 700px) { .ld13-lead { display: none; } .ld13-side-inner { gap: 18px; } }",

  /* Main area */
  ".ld13-main { position: relative; flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; align-items: center; padding: 20px clamp(12px, 4vw, 24px) 16px; box-sizing: border-box; }",
  "@media (min-width: 1024px) { .ld13-main { padding: 32px clamp(24px, 4vw, 72px) 22px; } }",
  ".ld13-main-deco { position: absolute; inset: 0; z-index: 0; pointer-events: none; background-image: radial-gradient(rgba(100,116,139,0.18) 1px, transparent 1px), radial-gradient(ellipse 60% 40% at 90% 0%, rgba(37,99,235,0.09) 0%, transparent 70%); background-size: 22px 22px, 100% 100%; }",
  ".ld13-center { position: relative; z-index: 1; width: 100%; max-width: 460px; min-width: 0; margin: auto 0; padding: 8px 0; }",
  ".ld13-foot { position: relative; z-index: 1; margin: 14px 0 0; font-size: .75rem; color: " + MUTED + "; text-align: center; overflow-wrap: anywhere; }",

  /* Card */
  ".login13-root .ld13-card { position: relative; overflow: hidden; min-width: 0; border: 1px solid #cfd8e6; border-radius: 18px; background: #fff; box-shadow: 0 1px 2px rgba(15,23,42,0.06), 0 28px 56px -30px rgba(15,23,42,0.35); }",
  ".login13-root .ld13-card::before { content: ''; position: absolute; left: 0; right: 0; top: 0; height: 4px; background: linear-gradient(90deg, #1d4ed8, #38bdf8); pointer-events: none; z-index: 2; }",
  ".login13-root .ld13-head { flex-direction: row; align-items: center; gap: 14px; padding: clamp(22px, 4vw, 30px) clamp(18px, 4.5vw, 30px) 18px; }",
  ".ld13-headicon { width: 46px; height: 46px; border-radius: 12px; background: " + NAVY + "; color: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }",
  ".ld13-headtext { min-width: 0; display: flex; flex-direction: column; gap: .3rem; }",
  ".login13-root .ld13-title { font-size: clamp(1.35rem, 5vw, 1.6rem); font-weight: 700; letter-spacing: -.4px; line-height: 1.15; color: " + INK + "; overflow-wrap: anywhere; }",
  ".login13-root .ld13-desc { font-size: .86rem; line-height: 1.45; color: " + MUTED + "; overflow-wrap: anywhere; }",
  ".login13-root .ld13-card .ui-card-content { padding: 0 clamp(18px, 4.5vw, 30px) 8px; }",
  ".login13-root .ld13-card .ui-card-footer { padding: 14px clamp(18px, 4.5vw, 30px) 24px; justify-content: center; text-align: center; font-size: .875rem; color: " + MUTED + "; }",

  /* Fields */
  ".ld13-group { margin-top: 16px; }",
  ".ld13-group:first-child { margin-top: 0; }",
  ".ld13-labelrow { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 8px; min-height: 44px; }",
  ".ld13-labelrow .ld13-label { margin-bottom: 0; }",
  ".login13-root .ld13-label { font-size: .84rem; font-weight: 600; color: " + INK + "; margin-bottom: 8px; }",
  ".ld13-field { position: relative; }",
  ".login13-root .ld13-field .ui-input { height: 3.25rem; min-height: 44px; min-width: 0; border: 1.5px solid #8fa1bc; border-radius: 12px; background: #f1f5fb; color: " + INK + "; -webkit-text-fill-color: " + INK + "; caret-color: #1d4ed8; opacity: 1; padding: 0 3rem 0 2.9rem; font-size: 16px; font-weight: 500; }",
  ".login13-root .ld13-field-pw .ui-input { padding-right: 5.2rem; }",
  ".login13-root .ld13-field .ui-input::placeholder { color: #5b6b82; -webkit-text-fill-color: #5b6b82; opacity: 1; }",
  ".login13-root .ld13-field .ui-input:hover { border-color: #3b6fd4; }",
  ".login13-root .ld13-field .ui-input:focus, .login13-root .ld13-field .ui-input:focus-visible { background: #fff; border-color: #1d4ed8; box-shadow: 0 0 0 4px rgba(29,78,216,0.18); }",
  ".login13-root .ld13-field .ui-input:-webkit-autofill, .login13-root .ld13-field .ui-input:-webkit-autofill:hover, .login13-root .ld13-field .ui-input:-webkit-autofill:active { -webkit-text-fill-color: " + INK + " !important; color: " + INK + " !important; caret-color: " + INK + "; -webkit-box-shadow: 0 0 0 1000px #e6eefb inset !important; box-shadow: 0 0 0 1000px #e6eefb inset !important; }",
  ".login13-root .ld13-field .ui-input:-webkit-autofill:focus { -webkit-text-fill-color: " + INK + " !important; -webkit-box-shadow: 0 0 0 1000px #ffffff inset, 0 0 0 4px rgba(29,78,216,0.18) !important; box-shadow: 0 0 0 1000px #ffffff inset, 0 0 0 4px rgba(29,78,216,0.18) !important; }",
  ".login13-root .ld13-field .ui-input:autofill { -webkit-text-fill-color: " + INK + "; color: " + INK + "; box-shadow: 0 0 0 1000px #e6eefb inset; }",
  ".ld13-fieldicon { position: absolute; left: .95rem; top: 50%; margin-top: -11px; z-index: 1; color: #4b5b73; pointer-events: none; }",
  ".ld13-field:focus-within .ld13-fieldicon { color: #1d4ed8; }",
  ".ld13-capsicon { position: absolute; right: .95rem; top: 50%; margin-top: -10px; z-index: 1; pointer-events: none; }",
  ".login13-root .ld13-capsicon-pw { right: 3.4rem; }",
  ".login13-root .ld13-toggle { position: absolute; right: .25rem; top: 50%; margin-top: -22px; z-index: 2; height: 44px; width: 44px; color: #3d4d66; border-radius: 10px; }",
  ".login13-root .ld13-toggle:hover { color: #1d4ed8; background: rgba(37,99,235,0.1); }",
  ".ld13-hint { min-height: 1.4rem; padding-top: .3rem; font-size: 12px; color: #92400e; display: flex; align-items: center; gap: .35rem; }",
  ".login13-root .ld13-link { color: #1d4ed8; text-decoration: none; font-weight: 600; font-size: .84rem; display: inline-flex; align-items: center; min-height: 44px; padding: 0 2px; border-radius: 4px; }",
  ".login13-root .ld13-link:hover { text-decoration: underline; text-underline-offset: 3px; }",
  ".login13-root .ld13-link:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }",
  ".login13-root .ld13-alert { display: flex; align-items: flex-start; gap: .6rem; background: #fef2f2; border: 1px solid #f1a9a9; color: #9f1515; font-weight: 500; font-size: .84rem; margin-bottom: 1rem; border-radius: 12px; overflow-wrap: anywhere; }",

  /* Buttons */
  ".login13-root .ld13-primary { width: 100%; height: 3.3rem; min-height: 44px; border-radius: 12px; font-size: .98rem; font-weight: 600; letter-spacing: .2px; color: #fff; background-color: #1d4ed8; background-image: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%); box-shadow: 0 10px 22px -10px rgba(29,78,216,0.7), inset 0 1px 0 rgba(255,255,255,0.18); }",
  ".login13-root .ld13-primary:hover { opacity: 1; background-image: linear-gradient(180deg, #1f5ae6 0%, #1741b8 100%); }",
  ".login13-root .ld13-primary:disabled { opacity: .65; box-shadow: none; }",
  ".login13-root .ld13-primary svg { transition: transform .2s ease; }",
  ".login13-root .ld13-primary:hover svg { transform: translateX(4px); }",
  ".login13-root .ld13-bio { width: 100%; height: 3rem; min-height: 44px; margin-top: .75rem; border-radius: 12px; font-weight: 600; color: #1d4ed8; border-color: #9db8ec; }",

  /* Company cards */
  ".ld13-companylist { display: flex; flex-direction: column; gap: 10px; max-height: min(46vh, 340px); overflow-y: auto; overflow-x: hidden; padding: 4px; margin: 0 -4px 16px; }",
  ".login13-root .ld13-company { position: relative; display: flex; flex-direction: row; align-items: center; gap: 14px; min-height: 60px; min-width: 0; padding: 10px 14px; border-radius: 14px; border: 1.5px solid #b9c6da; background: #f5f8fd; color: " + INK + "; cursor: pointer; outline: none; box-shadow: none; }",
  ".ld13-company::before, .ld13-company::after { content: ''; position: absolute; inset: -1.5px; border-radius: inherit; opacity: 0; pointer-events: none; z-index: 0; transition: opacity .18s ease; }",
  ".ld13-company::before { border: 1.5px solid #6ea0f5; }",
  ".ld13-company::after { border: 2px solid #1d4ed8; background: rgba(29,78,216,0.06); }",
  ".ld13-company:hover::before { opacity: 1; }",
  ".ld13-company[aria-checked='true']::after { opacity: 1; }",
  ".login13-root .ld13-company:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 4px #2563eb; }",
  ".ld13-company > * { position: relative; z-index: 1; }",
  ".ld13-avatar { width: 42px; height: 42px; border-radius: 11px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 1rem; color: #1e3a8a; background: #e1eafa; }",
  ".ld13-company[aria-checked='true'] .ld13-avatar { color: #fff; background: " + NAVY + "; }",
  ".ld13-companyname { flex: 1; min-width: 0; font-weight: 600; font-size: .94rem; color: " + INK + "; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }",
  ".ld13-radio { width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border: 2px solid #8aa0c2; background: #fff; color: #fff; }",
  ".ld13-company[aria-checked='true'] .ld13-radio { background: #1d4ed8; border-color: #1d4ed8; }",
  ".ld13-countrow { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 10px; font-size: .78rem; color: " + MUTED + "; flex-wrap: wrap; }",

  /* Session dialog */
  ".login13-root .ld13-signout { min-width: 82px; min-height: 44px; flex-shrink: 0; font-size: .78rem; font-weight: 700; border-radius: 10px; }",
  ".login13-root .ld13-cancel { width: 100%; min-height: 44px; font-weight: 600; color: " + MUTED + "; border-radius: 12px; }",

  "@media (prefers-reduced-motion: reduce) { .login13-root, .login13-root *, .login13-root *::before, .login13-root *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; } }",
].join("\n");

function LoginDesign13({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();
  const reduce = useReducedMotion();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: "#1d4ed8" }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  // ── Visual helpers (styling / motion only) ─────────────────────────────────
  // Each element owns its initial/animate (no parent variant chain).
  const rise = (delay = 0, fade = true) => ({
    initial: fade ? { opacity: 1, y: reduce ? 0 : 14 } : { y: reduce ? 0 : 14 },
    animate: fade ? { opacity: 1, y: 0 } : { y: 0 },
    transition: { duration: reduce ? 0.15 : 0.5, delay: reduce ? 0 : delay, ease: EASE },
  });

  // Keyboard support for the company radio cards
  const handleCompanyKeyDown = (e, idx) => {
    const key = e.key;
    if (key === " " || key === "Enter") {
      e.preventDefault();
      setSelectedCompany(companies[idx]);
      return;
    }
    let next = null;
    if (key === "ArrowDown" || key === "ArrowRight") next = (idx + 1) % companies.length;
    if (key === "ArrowUp" || key === "ArrowLeft") next = (idx - 1 + companies.length) % companies.length;
    if (next === null) return;
    e.preventDefault();
    setSelectedCompany(companies[next]);
    const el = document.getElementById("ld13-company-" + next);
    if (el) el.focus();
  };

  const capsHelper = (
    <div className="ld13-hint" role="status" aria-live="polite">
      <AnimatePresence initial={false}>
        {capsLockOn && (
          <motion.span
            key="caps"
            initial={{ opacity: 1, y: reduce ? 0 : -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0.1 : 0.18 }}
            style={{ display: "inline-flex", alignItems: "center", gap: ".35rem" }}
          >
            <WarningAmberIcon sx={{ fontSize: 14, color: "#d97706" }} />Caps Lock is ON
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );

  const errorAlert = (
    <AnimatePresence initial={false}>
      {errorMessage && (
        <motion.div
          key="err"
          initial={{ opacity: 1, y: reduce ? 0 : -8 }}
          animate={{ opacity: 1, y: 0, x: reduce ? 0 : [0, -7, 7, -4, 4, 0] }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.15 : 0.45 }}
        >
          <Alert variant="destructive" className="ld13-alert">
            <WarningAmberIcon sx={{ fontSize: 18, flexShrink: 0, mt: "1px" }} />
            <span style={{ minWidth: 0 }}>{errorMessage}</span>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <ThemeScope vars={THEME_VARS} className="login13-root ld13-page">
      <style>{CSS}</style>

      {/* ── COMPACT BRAND BAR (below 1024px) ── */}
      <header className="ld13-mbar">
        <div className="ld13-mbar-deco" aria-hidden="true" />
        <div className="ld13-mbar-top">
          <img className="ld13-logo" src="/RBLOGOBLUE.svg" alt="ReonBook" style={{ height: 34 }} />
          <Badge variant="outline" className="ld13-eyebrow">Enterprise Accounting Platform</Badge>
        </div>
      </header>

      {/* ── NAVY SIDEBAR (1024px and up) ── */}
      <aside className="ld13-side" aria-label="About ReonBook">
        <div className="ld13-side-deco" aria-hidden="true" />
        <div className="ld13-side-inner">
          <motion.div {...rise(0.05)}>
            <img className="ld13-logo" src="/RBLOGOBLUE.svg" alt="ReonBook" />
          </motion.div>

          <div>
            <motion.div {...rise(0.12)}>
              <Badge variant="outline" className="ld13-eyebrow">
                <span className="ld13-dot" aria-hidden="true" />
                Enterprise Accounting Platform
              </Badge>
            </motion.div>
            <motion.h2 className="ld13-h1" {...rise(0.18)}>
              Run your books.<br /><em>Sign in securely.</em>
            </motion.h2>
            <motion.p className="ld13-lead" {...rise(0.24)}>
              Accounting, GST and reporting for every company you run, in one place.
            </motion.p>

          </div>

          <div className="ld13-side-foot">
            <Separator />
            <div className="ld13-pills">
              <Badge variant="outline" className="ld13-pill">Multi-company</Badge>
              <Badge variant="outline" className="ld13-pill">Role-based access</Badge>
              
            </div>
          </div>
        </div>
      </aside>

      {/* ── MAIN (FORM) ── */}
      <main className="ld13-main">
        <div className="ld13-main-deco" aria-hidden="true" />

        <div className="ld13-center">
          <MotionCard
            className="ld13-card"
            initial={{ y: reduce ? 0 : 24, scale: reduce ? 1 : 0.985 }}
            animate={{ y: 0, scale: 1 }}
            transition={{ duration: reduce ? 0.15 : 0.7, ease: EASE }}
          >
            <AnimatePresence mode="wait">
              {!isCompanySelectionVisible ? (

                /* ── LOGIN FORM ── */
                <motion.div
                  key="login"
                  initial={false}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: reduce ? 0 : -18, transition: { duration: 0.16 } }}
                >
                  <CardHeader className="ld13-head">
                    <div className="ld13-headicon" aria-hidden="true"><LockOutlinedIcon sx={{ fontSize: 24 }} /></div>
                    <motion.div className="ld13-headtext" {...rise(0.1)}>
                      <CardTitle role="heading" aria-level={1} className="ld13-title">Welcome</CardTitle>
                      <CardDescription className="ld13-desc">Sign in to access your ReonBook dashboard</CardDescription>
                    </motion.div>
                  </CardHeader>

                  <CardContent>
                    <form onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>

                      <motion.div className="ld13-group" {...rise(0.14, false)}>
                        <Label htmlFor="ld13-identifier" className="ld13-label" style={{ display: "block" }}>
                          Username, Email or Mobile
                        </Label>
                        <div className="ld13-field ld13-field-id">
                          <PersonOutlineIcon className="ld13-fieldicon" sx={{ fontSize: 22 }} aria-hidden="true" />
                          <Input
                            id="ld13-identifier"
                            name="username"
                            type="text"
                            autoComplete="username"
                            placeholder="Username, email or mobile number"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                          />
                          {capsLockOn && <WarningAmberIcon className="ld13-capsicon" sx={{ color: "#d97706", fontSize: 20 }} aria-hidden="true" />}
                        </div>
                        {capsHelper}
                      </motion.div>

                      <motion.div className="ld13-group" {...rise(0.2, false)}>
                        <div className="ld13-labelrow">
                          <Label htmlFor="ld13-password" className="ld13-label">Password</Label>
                          <Link to="/forgot-password" className="ld13-link">Forgot password?</Link>
                        </div>
                        <div className="ld13-field ld13-field-pw">
                          <LockOutlinedIcon className="ld13-fieldicon" sx={{ fontSize: 22 }} aria-hidden="true" />
                          <Input
                            id="ld13-password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            placeholder="Enter your password"
                            value={userPassword}
                            onChange={(e) => setUserPassword(e.target.value)}
                            onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                          />
                          {capsLockOn && <WarningAmberIcon className="ld13-capsicon ld13-capsicon-pw" sx={{ color: "#d97706", fontSize: 20 }} aria-hidden="true" />}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="ld13-toggle"
                            onClick={handleTogglePasswordVisibility}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            aria-pressed={showPassword}
                          >
                            {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                          </Button>
                        </div>
                        {capsHelper}
                      </motion.div>

                      <motion.div style={{ marginTop: ".5rem" }} {...rise(0.26)}>
                        {errorAlert}

                        <MotionButton
                          type="submit"
                          size="lg"
                          disabled={isLoading}
                          aria-busy={isLoading}
                          className="ld13-primary"
                          whileHover={reduce || isLoading ? undefined : { y: -2 }}
                          whileTap={reduce || isLoading ? undefined : { scale: 0.98 }}
                          transition={{ type: "spring", stiffness: 400, damping: 22 }}
                        >
                          {isLoading
                            ? <><CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />Signing in...</>
                            : <>Sign In<ArrowForwardIcon sx={{ fontSize: 20 }} /></>}
                        </MotionButton>

                        {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                          <Button
                            type="button"
                            variant="outline"
                            className="ld13-bio"
                            onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}>
                            Login with Biometric
                          </Button>
                        )}
                      </motion.div>
                    </form>

                    <Separator style={{ margin: "1.25rem 0 0" }} />
                  </CardContent>

                  <CardFooter>
                    <p style={{ margin: 0 }}>
                      Don't have an account?{" "}
                      <Link to="/register" className="ld13-link" style={{ fontSize: "inherit", fontWeight: 700 }}>
                        Sign up now
                      </Link>
                    </p>
                  </CardFooter>
                </motion.div>

              ) : (

                /* ── COMPANY SELECTION ── */
                <motion.div
                  key="company"
                  initial={{ opacity: 1, x: reduce ? 0 : 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: reduce ? 0 : -18, transition: { duration: 0.16 } }}
                  transition={{ duration: reduce ? 0.15 : 0.4, ease: EASE }}
                >
                  <CardHeader className="ld13-head">
                    <div className="ld13-headicon" aria-hidden="true"><BusinessIcon sx={{ fontSize: 24 }} /></div>
                    <div className="ld13-headtext">
                      <CardTitle role="heading" aria-level={1} className="ld13-title">Select Organization</CardTitle>
                      <CardDescription className="ld13-desc" id="ld13-company-desc">Choose which organization to access</CardDescription>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div className="ld13-countrow">
                      <Badge variant="secondary">{companies.length} organization{companies.length !== 1 ? "s" : ""}</Badge>
                      <span>Use arrow keys to move</span>
                    </div>

                    <div
                      className="ld13-companylist"
                      role="radiogroup"
                      aria-label="Choose your organization"
                      aria-describedby="ld13-company-desc"
                    >
                      {companies.map((c, idx) => {
                        const isSel = selectedCompany?.companyId === c.companyId;
                        const firstFocusable = !selectedCompany && idx === 0;
                        return (
                          <motion.div
                            key={c.companyId}
                            style={{ minWidth: 0 }}
                            initial={{ opacity: 1, y: reduce ? 0 : 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: reduce ? 0.15 : 0.4, delay: reduce ? 0 : 0.08 + Math.min(idx, 8) * 0.05, ease: EASE }}
                          >
                            <MotionCard
                              id={"ld13-company-" + idx}
                              role="radio"
                              aria-checked={isSel}
                              tabIndex={isSel || firstFocusable ? 0 : -1}
                              className="ld13-company"
                              onClick={() => setSelectedCompany(companies.find((x) => x.companyId === c.companyId))}
                              onKeyDown={(e) => handleCompanyKeyDown(e, idx)}
                              whileHover={reduce ? undefined : { y: -2 }}
                              whileTap={reduce ? undefined : { scale: 0.99 }}
                              transition={{ type: "spring", stiffness: 380, damping: 24 }}
                            >
                              <span className="ld13-avatar" aria-hidden="true">{(c.companyName || "?").trim().charAt(0).toUpperCase()}</span>
                              <span className="ld13-companyname" title={c.companyName}>{c.companyName}</span>
                              <span className="ld13-radio" aria-hidden="true">
                                <AnimatePresence initial={false}>
                                  {isSel && (
                                    <motion.span
                                      key="tick"
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                      exit={{ scale: 0 }}
                                      transition={{ duration: reduce ? 0.05 : 0.18 }}
                                      style={{ display: "flex" }}
                                    >
                                      <CheckIcon sx={{ fontSize: 14 }} />
                                    </motion.span>
                                  )}
                                </AnimatePresence>
                              </span>
                            </MotionCard>
                          </motion.div>
                        );
                      })}
                    </div>

                    {errorAlert}

                    <MotionButton
                      type="button"
                      size="lg"
                      className="ld13-primary"
                      onClick={() => FN_SelectCompany()}
                      whileHover={reduce ? undefined : { y: -2 }}
                      whileTap={reduce ? undefined : { scale: 0.98 }}
                      transition={{ type: "spring", stiffness: 400, damping: 22 }}
                    >
                      Continue to Dashboard<ArrowForwardIcon sx={{ fontSize: 20 }} />
                    </MotionButton>
                    <div style={{ height: 20 }} />
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </MotionCard>
        </div>

        <p className="ld13-foot">
          <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.4 }} />
          {new Date().getFullYear()} Powered by{" "}
          Soltrack Technologies Pvt Ltd
        </p>
      </main>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="login13-root"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        scroll="body"
        aria-labelledby="ld13-session-title"
        slots={{ transition: Grow }}
        transitionDuration={{ enter: 320, exit: 180 }}
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(8,16,36,0.6)" } },
          paper: { sx: { borderRadius: "18px", overflow: "hidden", m: 2, width: "calc(100% - 32px)", maxWidth: 440 } },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ThemeScope vars={THEME_VARS} className="login13-root">
            {/* Top banner */}
            <Box sx={{ background: NAVY, borderBottom: "4px solid #2563eb", px: { xs: 2, sm: 3 }, pt: 3, pb: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Box sx={{ width: 42, height: 42, borderRadius: "12px", bgcolor: "rgba(96,165,250,0.2)", border: "1px solid rgba(147,197,253,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <DevicesIcon sx={{ color: "#fff", fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography id="ld13-session-title" component="h2" sx={{ fontWeight: 700, fontSize: "1.08rem", color: "#fff", lineHeight: 1.3 }}>
                    Session Limit Reached
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: "#c3d3ee", mt: 0.2 }}>
                    {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ bgcolor: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: "10px", px: 2, py: 1 }}>
                <Typography sx={{ fontSize: "0.8rem", color: "#fff", lineHeight: 1.5 }}>
                  Sign out of one of the devices below to log in on this device.
                </Typography>
              </Box>
            </Box>

            {/* Device list */}
            <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2, pb: 1 }}>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: MUTED, letterSpacing: "0.8px", textTransform: "uppercase", mb: 1.5 }}>
                Active Sessions
              </Typography>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <Box key={session.id}
                  component={motion.div}
                  initial={{ opacity: 1, y: reduce ? 0 : 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: reduce ? 0 : 0.1 + idx * 0.07, ease: EASE }}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5, minWidth: 0,
                    p: 1.5, mb: 1, borderRadius: "12px",
                    border: "1px solid #b9c6da",
                    bgcolor: "#f5f8fd",
                  }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: "10px", bgcolor: "#dbe7fb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {deviceIcon(session.device_type)}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 600, fontSize: "0.87rem", color: INK }}>
                      {session.device_name || "Unknown Device"}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3, flexWrap: "wrap" }}>
                      <AccessTimeIcon sx={{ fontSize: 12, color: MUTED }} />
                      <Typography sx={{ fontSize: "0.73rem", color: MUTED }}>
                        {timeAgo(session.last_activity)}
                      </Typography>
                      {session.ip_address && (
                        <Typography sx={{ fontSize: "0.73rem", color: MUTED, ml: 0.5, wordBreak: "break-all" }}>
                          · {session.ip_address}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="ld13-signout"
                    disabled={kickingSessionId === session.id}
                    onClick={async () => {
                      setKickingSessionId(session.id);
                      setSessionPickerData(null);
                      await FN_HandleLogin(identifier, userPassword, session.id);
                      setKickingSessionId(null);
                    }}
                  >
                    {kickingSessionId === session.id
                      ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                      : "Sign Out"}
                  </Button>
                </Box>
              ))}
            </Box>

            {/* Cancel */}
            <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: 2.5, pt: 0.5 }}>
              <Button
                type="button"
                variant="outline"
                className="ld13-cancel"
                onClick={() => setSessionPickerData(null)}
              >
                Cancel
              </Button>
            </Box>
          </ThemeScope>
        </DialogContent>
      </Dialog>

    </ThemeScope>
  );
}

export default LoginDesign13;
